/**
 * LoadingButton Component
 *
 * Purpose: Button component with built-in loading state indicator
 *          Replaces @mui/lab LoadingButton to avoid dependency on experimental package
 *
 * Architecture: Simple wrapper around Material-UI Button with CircularProgress
 *
 * Dependencies: React, Material-UI
 * Author: Development Team
 * Created: 2025-01-27
 */

import React from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';

export interface LoadingButtonProps extends Omit<ButtonProps, 'startIcon'> {
  /** Show loading spinner and disable button */
  loading?: boolean;
  /** Icon to show before text (hidden when loading) */
  startIcon?: React.ReactNode;
  /** Icon to show after text (hidden when loading) */
  endIcon?: React.ReactNode;
}

/**
 * LoadingButton - Button with loading state
 *
 * Shows a CircularProgress spinner when loading is true and disables the button.
 * Hides startIcon and endIcon when loading to make room for the spinner.
 */
export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  disabled,
  startIcon,
  endIcon,
  children,
  ...props
}) => {
  return (
    <Button
      {...props}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : startIcon}
      endIcon={loading ? undefined : endIcon}
    >
      {children}
    </Button>
  );
};

export default LoadingButton;
